import React, { useEffect, useState } from 'react';
import { easings, useSpring } from 'react-spring';
import { useTranslation } from 'react-i18next';
import { Label, ShowFiltersCTA, ShowFilterAlphaWrapper } from './ShowFilterCTA.styles';
import FilterIcon from '../../assets/icons/FilterIcon';
import { COLORS } from '../../styles/variables';
import useComplexAnimations from '../../hooks/Animations/useComplexAnimations';
import { device } from '../../styles/devices';
import { useMediaQuery } from 'react-responsive';
import { TextButton } from '../../../design-system';

interface ShowFilterCTATypes {
  isFiltersOpen: boolean;
  setIsFiltersOpen: (arg: boolean) => void;
  scrolled: boolean;
}
const ShowFilterCTA: React.FC<ShowFilterCTATypes> = ({
  isFiltersOpen,
  setIsFiltersOpen,
  scrolled,
}) => {
  const isLaptop = useMediaQuery({ query: device.laptop });

  const isAlpha = !process.env.IS_LEGACY;

  const { t } = useTranslation();

  const [isHovered, setIsHovered] = useState(false);

  const { easeInSine } = easings;

  const [buttonHeight, setButtonHeight] = useSpring(() => ({
    height: 100,
    config: {
      duration: 100,
      easing: easeInSine,
    },
  }));
  const [labelAppearence, setLabelAppearence] = useSpring(() => ({
    opacity: 1,
    display: 'auto',
    config: {
      duration: 100,
      easing: easeInSine,
    },
  }));

  useEffect(() => {
    if (!scrolled) {
      setLabelAppearence({
        opacity: 1,
        display: 'inline-block',
        config: {
          duration: 100,
          easing: easeInSine,
        },
      });
      setButtonHeight({ height: 100 });
    }
    if (scrolled) {
      setLabelAppearence({
        opacity: 0,
        display: 'none',
        config: {
          duration: 100,
          easing: easeInSine,
        },
      });
      setButtonHeight({ height: 50 });
    }
  }, [easeInSine, scrolled, setButtonHeight, setLabelAppearence]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    setLabelAppearence({
      opacity: 1,
      display: 'inline-block',
      config: {
        duration: 100,
        easing: easeInSine,
      },
    });
    setButtonHeight({ height: 100 });
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (scrolled) {
      setLabelAppearence({
        opacity: 0,
        display: 'none',
        config: {
          duration: 100,
          easing: easeInSine,
        },
      });
      setButtonHeight({ height: 50 });
    }
  };

  const { showFiltersDesktopAnimation } = useComplexAnimations(isFiltersOpen);

  if (!isLaptop) return null;

  if (isAlpha) {
    return (
      <ShowFilterAlphaWrapper $scrolled={scrolled}>
        <TextButton
          icon="options"
          iconPosition="leading"
          variant="tertiary"
          size="md"
          onClick={() => setIsFiltersOpen(true)}
          aria-label={t('promo.filters.showFilters')}
        >
          {t('promo.filters.showFilters')}
        </TextButton>
      </ShowFilterAlphaWrapper>
    );
  }

  return (
    <ShowFiltersCTA
      onClick={() => {
        setIsFiltersOpen(true);
      }}
      style={{
        ...showFiltersDesktopAnimation,
        ...buttonHeight,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      $scrolled={scrolled}
      $ishovered={isHovered}
    >
      <FilterIcon color={COLORS.black} />
      <Label style={{ ...labelAppearence }}>{t('promo.filters.showFilters')}</Label>
    </ShowFiltersCTA>
  );
};

export default ShowFilterCTA;
